import { useState } from 'react'

import { fontWeightBold } from '@/constants'
import { useMutation } from '@api/hooks'
import IconWithPopover from '@components/IconWithPopover/IconWithPopover'
import { Contract as ContractType } from '@domain'
import { LoadingButton } from '@mui/lab'
import { Box, Button, Divider, Paper, Stack, Typography } from '@mui/material'
import { MarketBasketsKeys } from '@services/marketBaskets.api'
import ProgramsApi, { ProgramsKeys } from '@services/programs.api'
import { useQueryClient } from '@tanstack/react-query'
import { numToCurrency } from '@utils/stringHelpers'
import { useMarketBasketContext } from '@views/MarketBaskets/MarketBaskets.hooks'
import { useParams } from 'react-router-dom'

import Property from './Property'

export default function Contract({
	contract,
	disableSelection,
}: {
	contract: ContractType
	disableSelection: boolean
}) {
	const { marketBasketId, similarProductId } = useParams()

	const queryClient = useQueryClient()

	const { mutate, isPending } = useMutation({
		mutationFn: ProgramsApi.selectProgram,
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: MarketBasketsKeys.marketBasket(marketBasketId),
			})
			queryClient.invalidateQueries({
				queryKey: ProgramsKeys.basketPrograms(marketBasketId, similarProductId),
			})
		},
	})

	const handleSelect = () => {
		mutate({
			marketBasketId,
			similarProductId,
			programId: contract.id,
			programType: 'contract',
		})
	}

	return (
		<ContractUi
			contract={contract}
			selectionLoading={isPending}
			onSelect={handleSelect}
			disableSelection={disableSelection}
		/>
	)
}

export function ContractUi({
	contract,
	disableSelection,
	selectionLoading,
	onSelect,
}: {
	contract: ContractType
	disableSelection: boolean
	selectionLoading: boolean
	onSelect: () => void
}) {
	const [expanded, setExpanded] = useState(false)
	const { marketBasket } = useMarketBasketContext()
	const currency = marketBasket?.currency || 'USD'

	const markup = `(${contract.markup}) ${contract.markupType} = ${numToCurrency(contract.markupAmount, currency)}`
	const disqualified = contract.disqualificationReason !== null || disableSelection
	const disqualifiedReason = disableSelection
		? `Not a matched product.\n ${contract.disqualificationReason || ''}`
		: contract.disqualificationReason

	const handleExpand = () => {
		setExpanded(!expanded)
	}

	const selectButton = (
		<Stack direction="row" spacing={1}>
			{disqualified && <IconWithPopover>{disqualifiedReason}</IconWithPopover>}
			<LoadingButton
				color="secondary"
				variant="outlined"
				disabled={disqualified}
				onClick={onSelect}
				loading={selectionLoading}
			>
				Select
			</LoadingButton>
		</Stack>
	)

	return (
		<Paper
			variant="outlined"
			sx={{
				padding: '1rem',
				width: '100%',
				color: disqualified ? 'text.disabled' : 'text.primary',
				borderColor: contract.selected ? 'secondary.main' : 'gray[200]',
			}}
		>
			{contract && (
				<Stack spacing={1}>
					<Stack direction="row" justifyContent="space-between" alignItems="center">
						<Typography variant="h6" color={contract.selected ? 'secondary.main' : 'text.primary'}>
							{contract.termSetName}
						</Typography>
						{contract.selected ? (
							<Box sx={{ py: 0.5, px: 1, borderRadius: '4px', backgroundColor: 'secondary.main' }}>
								<Typography variant="body2" color="background.paper" fontWeight={fontWeightBold}>
									SELECTED
								</Typography>
							</Box>
						) : (
							selectButton
						)}
					</Stack>
					<Property
						label="Contracted Price"
						value={numToCurrency(contract.contractedPrice, currency)}
					/>
					<Property label="Unit Savings" value={numToCurrency(contract.unitSavings, currency)} />
					<Property label="Tier" value={contract.tier === '' ? 'All' : contract.tier} />
					<Property label="Contract ID" value={contract.programId} />
					<Property label="Term Set ID" value={contract.termSetId} />
					<Property label="Term Set Name" value={contract.termSetName} />
					<Property label="Markup" value={markup} />
					<Property label="On Program" value={contract.onProgram ? 'Yes' : 'No'} />

					{expanded && (
						<>
							<Divider />
							<Property label="Pricing Value Type" value={contract.pricingValueType} />
							<Property label="Pricing Value Adjusted" value={contract.pricingValueAdjusted} />
							<Property label="CategoryName" value={contract.categoryName} />
							<Property label="Brand Type" value={contract.brandType} />
							<Property label="Invoice Qty" value={contract.invoiceQuantity} />
							<Property label="Invoice Unit" value={contract.invoiceUnit} />
							<Property label="Stocked" value={contract.stocked} />
							<Property label="Pack Conversion" value={contract.packConversion} />
							<Property label="Exclusion" value={contract.exclusion ? 'Yes' : 'No'} />
						</>
					)}
				</Stack>
			)}
			<Button color="secondary" variant="text" onClick={handleExpand} sx={{ pt: 2 }}>
				{expanded ? 'less' : 'more'}
			</Button>
		</Paper>
	)
}
